import { h } from "preact";
import { useState } from "preact/hooks";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { categoryLabel, formatDate, statusLabel, ticketContextLabel } from "../labels";

function formatAttachmentSize(size) {
  const value = Number(size || 0);
  if (value < 1024 * 1024) return `${Math.max(1, Math.round(value / 1024))} Ko`;
  return `${(value / 1024 / 1024).toFixed(1)} Mo`;
}

export function TicketThread({ ticket, loading, error, attachmentBaseUrl = "/api/support", onReply }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState("");

  if (loading) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress size={30} />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  if (!ticket) {
    return (
      <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
        <Typography color="text.secondary">Sélectionnez une demande pour afficher les échanges.</Typography>
      </Box>
    );
  }

  const readOnly = ticket.status === "resolved" || ticket.status === "closed";

  const submitReply = async (event) => {
    event.preventDefault();
    if (readOnly) return;
    const message = body.trim();
    if (!message) return;

    setSending(true);
    setReplyError("");

    try {
      await onReply({ body: message, lock_version: ticket.lock_version });
      setBody("");
    } catch (replyException) {
      setReplyError(replyException.message || "La réponse n’a pas pu être envoyée.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ minHeight: 420 }}>
      <Box>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
          <Typography variant="h6" component="h2">
            {categoryLabel(ticket.category)}
          </Typography>
          <Chip label={statusLabel(ticket.status)} size="small" sx={{ bgcolor: "background.paper" }} />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          {ticket.reference} · {ticketContextLabel(ticket)}
        </Typography>

        {(ticket.attachments || []).length ? (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Pièces jointes
            </Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {ticket.attachments.map((attachment) => (
                <Button
                  key={attachment.id}
                  component="a"
                  href={`${attachmentBaseUrl}/tickets/${ticket.id}/attachments/${attachment.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  variant="outlined"
                  sx={{ textTransform: "none", maxWidth: "100%" }}
                >
                  {attachment.original_name} · {formatAttachmentSize(attachment.size)}
                </Button>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Box>

      <Divider />

      <Stack spacing={1.5} sx={{ maxHeight: 360, overflowY: "auto", pr: 0.5 }}>
        {(ticket.messages || []).map((message) => {
          const fromRequester = message.author_type === "requester";

          return (
            <Paper
              key={message.id}
              variant="outlined"
              sx={{
                p: 1.5,
                maxWidth: "88%",
                alignSelf: fromRequester ? "flex-end" : "flex-start",
                bgcolor: fromRequester ? "action.selected" : "background.paper",
              }}
            >
              <Typography variant="caption" color="text.secondary" display="block">
                {fromRequester ? "Vous" : "Support"} · {formatDate(message.inserted_at)}
              </Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {message.body}
              </Typography>
            </Paper>
          );
        })}
      </Stack>

      {readOnly ? (
        <Alert severity="info" sx={{ mt: "auto" }}>
          Ce ticket est terminé. Il n’est plus possible d’ajouter une réponse.
        </Alert>
      ) : (
        <Box component="form" onSubmit={submitReply} sx={{ mt: "auto" }}>
          {replyError ? <Alert severity="error" sx={{ mb: 1 }}>{replyError}</Alert> : null}
          <TextField
            label="Ajouter une réponse"
            value={body}
            onChange={(event) => setBody(event.currentTarget.value)}
            multiline
            minRows={3}
            fullWidth
            inputProps={{ maxLength: 12000 }}
            disabled={sending}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button type="submit" variant="contained" disabled={sending || !body.trim()}>
              {sending ? "Envoi…" : "Envoyer"}
            </Button>
          </Box>
        </Box>
      )}
    </Stack>
  );
}
